import { deriveProductSemanticTags, normalizeMenuBlob } from '@/lib/menuSemanticTags';
import { getProductUpsellItems } from '@/lib/productComposition';

const ACTIVE_PROMOS = (menuData) =>
  (menuData?.promos || []).filter((pr) => pr?.is_active !== false);

/** @param {string} id */
function semanticFamilyToReadable(id) {
  if (!id) return null;
  const map = {
    hamburguesa: 'hamburguesa',
    arma_burger: 'hamburguesa',
    sushi: 'sushi',
    hand_roll: 'roll',
    pizza: 'pizza',
    completo: 'completo',
    mex: 'taco',
    empanada: 'empanada',
    pollo_frito: 'pollo',
    pollo_asado: 'pollo',
    sandwich: 'sándwich',
    poke: 'poke',
    parrilla: 'parrilla',
    pasta: 'pasta',
    ramen: 'ramen',
    wok: 'wok',
    mechada: 'sándwich',
    papas_topping: 'porción',
    cerveza: 'cerveza',
    licor: 'trago',
    colaciones: 'colación',
    platos_preparados: 'plato',
  };
  return map[id] || null;
}

/** @param {'f' | 'm'} gender */
function phraseWithArticle(gender, noun) {
  const art = gender === 'f' ? 'Esta' : 'Este';
  return `${art} ${noun}`.trim();
}

const FAMILY_GENDER = {
  hamburguesa: 'f',
  pizza: 'f',
  empanada: 'f',
  colación: 'f',
  porción: 'f',
  sushi: 'm',
  roll: 'm',
  taco: 'm',
  completo: 'm',
  pollo: 'm',
  sándwich: 'm',
  poke: 'm',
  parrilla: 'f',
  pasta: 'f',
  ramen: 'm',
  wok: 'm',
  sandwich: 'm',
  cerveza: 'f',
  trago: 'm',
  plato: 'm',
  producto: 'm',
};

/**
 * Familia legible para copy (español). Fallback: "producto".
 * @param {object} product
 * @param {object} business
 * @param {object} menuData
 * @returns {string}
 */
export function resolveReadableProductFamily(product, business, menuData) {
  const tags = deriveProductSemanticTags(product, business || null, ACTIVE_PROMOS(menuData));
  const fromTag = semanticFamilyToReadable(tags.family);
  if (fromTag) return fromTag;

  const blob = normalizeMenuBlob(
    [product?.name, product?.category, product?.description, business?.category, business?.rubro]
      .filter(Boolean)
      .join(' ')
  );

  const rules = [
    [/\b(hamburguesa|burger|hamburg|smash)\b/, 'hamburguesa'],
    [/\b(uramaki|hosomaki|nigiri|sashimi|sushi)\b/, 'sushi'],
    [/\b(hand\s*roll|temaki)\b/, 'roll'],
    [/\b(pizza|piza)\b/, 'pizza'],
    [/\b(completo|hot\s*dog|vienesa)\b/, 'completo'],
    [/\b(taco|burrito|quesadilla|nacho)\b/, 'taco'],
    [/\b(empanada)\b/, 'empanada'],
    [/\b(pollo|chicken|alita|wing|broaster)\b/, 'pollo'],
    [/\b(roll\b|maki\b)/, 'roll'],
  ];
  for (const [re, label] of rules) {
    if (re.test(blob)) return label;
  }
  return 'producto';
}

/**
 * Frase natural con artículo demostrativo.
 * @param {object} product
 * @param {object} business
 * @param {object} menuData
 * @returns {string}
 */
export function resolveReadableProductPhrase(product, business, menuData) {
  const fam = resolveReadableProductFamily(product, business, menuData);
  const g = FAMILY_GENDER[fam] || 'm';
  return phraseWithArticle(g, fam);
}

/**
 * Sugerencias priorizadas desde ficha (upsell_items + nombres de add_ons), sin duplicar.
 * @param {object} product
 * @param {number} [max]
 * @returns {string[]}
 */
export function resolvePreferredUpsellSuggestions(product, max = 5) {
  const ups = getProductUpsellItems(product);
  const fromAddons = Array.isArray(product?.add_ons)
    ? product.add_ons.map((x) => (typeof x === 'string' ? x : x?.name)).filter(Boolean)
    : [];
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return;
    const key = normalizeMenuBlob(s);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };
  for (const x of ups) push(x);
  for (const x of fromAddons) push(x);
  return out.slice(0, max);
}

/**
 * Copy neutro si no hay sugerencias en ficha.
 * @param {object} product
 * @returns {string[]}
 */
export function resolveNeutralUpsellFallbackLines(product) {
  const hasAddons = Array.isArray(product?.add_ons) && product.add_ons.length > 0;
  if (hasAddons) {
    return ['Revisá los extras pagos en la ficha del producto 👆'];
  }
  return ['Si querés sumar algo, mirá la carta o los complementos que ofrezca el local 👆'];
}
